import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Truck, Wrench, Store, RotateCcw } from "lucide-react";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";

import { CartDrawer } from "@/components/CartDrawer";
import { ShopBrowser } from "@/components/shop/ShopBrowser";
import { Button } from "@/components/ui/button";

const CANONICAL = "https://www.akhusbilfritid.se/butik";
const TITLE = "Butik – tillbehör till husbil & husvagn | AK Husbil";
const DESC =
  "Tillbehör till husbil och husvagn: litiumbatteri, dieselvärmare, solpaneler, mover, fuktmätare, vattenkit och vinterskydd. Utvalt av verkstaden i Kalix.";

export const Route = createFileRoute("/butik/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Butik – tillbehör till husbil & husvagn" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  loader: async () => {
    try {
      return { products: await fetchProducts(40) };
    } catch (e) {
      console.error("Shopify products failed", e);
      return { products: [] as ShopifyProduct[] };
    }
  },
  component: ButikPage,
});

function ButikPage() {
  const { products } = Route.useLoaderData() as { products: ShopifyProduct[] };


  return (
    <div className="min-h-screen">
      <section className="border-b bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:py-14">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Store size={14} /> Butik
              </span>
              <h1 className="mt-2 text-2xl sm:text-4xl font-bold tracking-tight">
                Tillbehör vi själva använder
              </h1>
              <p className="mt-3 max-w-2xl text-sm sm:text-base text-muted-foreground">
                Vi säljer inte allt – bara det som håller. Varje produkt här är något vi använder i
                verkstaden i Kalix eller rekommenderar till kunder varje vecka: fukttest,
                uppställning, gasol, el och vinterskydd.
              </p>
            </div>
            <div className="hidden sm:block shrink-0">
              <CartDrawer />
            </div>
          </div>

          <div className="mt-5 sm:mt-6 grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-4">
            {[
              { icon: <ShieldCheck size={16} />, t: "Utvalt av verkstaden", d: "Testat i nordiskt klimat" },
              { icon: <Truck size={16} />, t: "Frakt i hela Sverige", d: "Spårbart, 1–2 arbetsdagar" },
              { icon: <RotateCcw size={16} />, t: "14 dagars ångerrätt", d: "3 års reklamationsrätt" },
              { icon: <Wrench size={16} />, t: "Hjälp att montera", d: "Boka tid hos oss i Kalix" },
            ].map((b) => (
              <div key={b.t} className="rounded-xl border bg-card p-3">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <span className="shrink-0">{b.icon}</span>
                  <span className="min-w-0 truncate">{b.t}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{b.d}</p>
              </div>
            ))}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            <Link to="/kopvillkor" className="underline underline-offset-4 hover:text-foreground">
              Köpvillkor, returer &amp; reklamation
            </Link>
          </p>
        </div>
      </section>


      <div className="sm:hidden sticky top-[64px] z-30 border-b bg-background/90 backdrop-blur px-4 py-2 flex items-center justify-between">
        <span className="text-sm font-medium">{products.length} produkter</span>
        <CartDrawer />
      </div>

      <section className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
        {products.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-10 text-center">
            <h2 className="text-lg font-semibold">Inga produkter hittades</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Butiken fylls på. Säg till i chatten vad du vill sälja och priset, så lägger vi upp
              det.
            </p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/reservdelar">Beställ reservdelar istället</Link>
            </Button>
          </div>
        ) : (
          <ShopBrowser products={products} />
        )}


        <div className="mt-12 rounded-2xl border bg-muted/30 p-6">
          <h2 className="text-lg font-semibold">Hittar du inte delen du söker?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Vi tar hem reservdelar till Rapido, Dreamer, Itineo, Adria, Fiat och Polar. Skicka en
            bild på delen – du får pris och leveranstid inom 24 timmar.
          </p>
          <Button asChild className="mt-4">
            <Link to="/reservdelar">Till reservdelar</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
